'use client';

import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

// Profile Dropdown Component
function ProfileDropdown({ user, userProfile, onSignOut, onRefreshProfile }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleRefreshProfile = async () => {
    setIsRefreshing(true);
    try {
      await onRefreshProfile();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSignOutClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (isSigningOut) return; // Prevent double-clicks

    console.log('🔴 Sign out button clicked');
    setIsSigningOut(true);
    setIsOpen(false);

    // Clear storage directly here too
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (err) {
      console.warn('Storage clear error:', err);
    }

    // Call sign out
    onSignOut();

    // BACKUP: Force redirect after 500ms if signOut doesn't redirect
    setTimeout(() => {
      console.log('🔄 Backup redirect triggered');
      window.location.href = '/login';
    }, 500);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-surface contemplative-transition"
      >
        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
          {userProfile?.full_name?.charAt(0)?.toUpperCase() || userProfile?.fullName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <Icon name="ChevronDownIcon" size={16} className="text-muted-foreground" />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          <div
            className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-50"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="p-4 border-b border-border">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    {userProfile?.full_name || userProfile?.fullName || 'User'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user?.email}
                  </p>
                  <div className="mt-1">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {userProfile?.role || 'member'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleRefreshProfile}
                  disabled={isRefreshing}
                  className="p-1 rounded-lg hover:bg-surface contemplative-transition disabled:opacity-50"
                  title="Refresh profile data"
                >
                  <Icon
                    name="ArrowPathIcon"
                    size={16}
                    className={`text-muted-foreground ${isRefreshing ? 'animate-spin' : ''}`}
                  />
                </button>
              </div>
            </div>

            <div className="p-2 space-y-1">
              <Link
                href="/personal-dashboard"
                className="flex items-center space-x-2 w-full px-3 py-2 text-sm !text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                onClick={(e) => {
                  e?.stopPropagation();
                  setIsOpen(false);
                }}
              >
                <Icon name="UserIcon" size={16} />
                <span>Dashboard</span>
              </Link>

              <Link
                href="/user-settings"
                className="flex items-center space-x-2 w-full px-3 py-2 text-sm !text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                onClick={(e) => {
                  e?.stopPropagation();
                  setIsOpen(false);
                }}
              >
                <Icon name="Cog6ToothIcon" size={16} />
                <span>Settings</span>
              </Link>

              <button
                type="button"
                disabled={isSigningOut}
                onClick={handleSignOutClick}
                className={`flex items-center space-x-2 w-full px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer ${isSigningOut
                  ? 'text-gray-400 bg-gray-100 cursor-wait' :'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                  }`}
              >
                {isSigningOut ? (
                  <>
                    <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                    <span>Signing out...</span>
                  </>
                ) : (
                  <>
                    <Icon name="ArrowRightOnRectangleIcon" size={16} />
                    <span>Sign Out & Clear Session</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

ProfileDropdown.propTypes = {
  user: PropTypes?.object?.isRequired,
  userProfile: PropTypes?.object,
  onSignOut: PropTypes?.func?.isRequired,
  onRefreshProfile: PropTypes?.func?.isRequired
};

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { user, userProfile, signOut, refreshProfile, loading } = useAuth();

  // Track mounted state to avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsMenuOpen(false);
    };

    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMenuOpen]);

  const handleSignOut = async () => {
    // Show immediate feedback
    setIsMenuOpen(false);

    try {
      console.log('🔄 Signing out...');
      // signOut now handles redirect internally
      await signOut(true);
    } catch (error) {
      console.error('Sign out error:', error);
      // Force navigation even on error
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
  };

  const handleRefreshProfile = async () => {
    try {
      const { error } = await refreshProfile();
      if (error) {
        console.error('Failed to refresh profile:', error);
      }
    } catch (error) {
      console.error('Refresh profile error:', error);
    }
  };

  const navigationItems = [
    { name: 'Home', href: '/homepage' },
    { name: 'Devotional', href: '/devotionals' },
    { name: 'Bible', href: '/bible-reader' },
    { name: 'Books', href: '/digital-books-library' },
    { name: 'Audios', href: '/audio-book-library' },
    { name: 'Live Stream', href: '/live-streams-hub' },
    { name: 'Community', href: '/community-hub' },
    { name: 'Dashboard', href: '/personal-dashboard' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 bg-card/95 backdrop-blur-sm border-b border-border z-50">
      <nav className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="BookOpenIcon" size={20} className="text-primary-foreground" />
            </div>
            <span className="text-xl font-bold font-crimson text-foreground">Christ Faculty</span>
          </Link>

          {/* Desktop Navigation - Restored and Always Visible on Large Screens */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigationItems?.map((item) => (
              <Link
                key={item?.name}
                href={item?.href}
                className="text-foreground hover:text-primary contemplative-transition font-medium"
              >
                {item?.name}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center space-x-4">
            {!mounted ? (
              // Server-side placeholder to avoid hydration mismatch
              (<div className="w-8 h-8 rounded-full bg-surface" />)
            ) : user ? (
              // Show profile dropdown when user is logged in (regardless of loading state)
              (<ProfileDropdown
                user={user}
                userProfile={userProfile}
                onSignOut={handleSignOut}
                onRefreshProfile={handleRefreshProfile}
              />)
            ) : loading ? (
              // Show loading state only when loading and no user
              (<div className="w-8 h-8 rounded-full bg-surface animate-pulse" />)
            ) : (
              // Show sign in/up buttons when not loading and no user
              (<div className="hidden lg:flex items-center space-x-2">
                <Link
                  href="/login"
                  className="px-4 py-2 text-foreground hover:text-primary contemplative-transition"
                >
                  Sign In
                </Link>
                <Link
                  href="/register"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 contemplative-transition"
                >
                  Sign Up
                </Link>
              </div>)
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={(e) => {
                e?.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              className="lg:hidden p-2 rounded-lg hover:bg-surface contemplative-transition"
            >
              <Icon name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-border mt-2 pt-4 pb-4 space-y-2">
            {navigationItems?.map((item) => (
              <Link
                key={item?.name}
                href={item?.href}
                className="block px-4 py-2 text-foreground hover:bg-surface rounded-lg contemplative-transition"
                onClick={() => setIsMenuOpen(false)}
              >
                {item?.name}
              </Link>
            ))}

            {mounted && (
              <div className="border-t border-border pt-4 mt-4">
                {user ? (
                  <div className="px-4 space-y-2">
                    <div className="flex items-center space-x-3 py-2">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                        {userProfile?.full_name?.charAt(0)?.toUpperCase() || userProfile?.fullName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-medium text-foreground text-sm">
                          {userProfile?.full_name || userProfile?.fullName || 'User'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {user?.email}
                        </p>
                      </div>
                    </div>

                    <Link
                      href="/personal-dashboard"
                      className="block py-2 text-foreground hover:text-primary contemplative-transition"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Dashboard
                    </Link>

                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        handleSignOut();
                      }}
                      className="block py-2 text-destructive hover:text-destructive/80 contemplative-transition w-full text-left"
                    >
                      Sign Out & Clear Session
                    </button>
                  </div>
                ) : loading ? (
                  <div className="px-4 py-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-surface animate-pulse" />
                      <div className="space-y-2">
                        <div className="h-4 w-24 bg-surface animate-pulse rounded" />
                        <div className="h-3 w-32 bg-surface animate-pulse rounded" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 space-y-2">
                    <Link
                      href="/login"
                      className="block py-2 text-foreground hover:text-primary contemplative-transition"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                    <Link
                      href="/register"
                      className="block py-2 text-primary hover:text-primary/80 contemplative-transition"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </nav>
    </header>
  );
}
